import React, { useState, useEffect, useMemo } from 'react';
import { 
  Clock, 
  Users,
  Calendar,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Search,
  Download,
  Filter,
  LogIn,
  LogOut,
  TrendingUp,
  Building2,
  Palmtree,
  Heart,
  Briefcase,
  Baby,
  Eye,
  Plus,
  Edit,
  Save,
  Upload,
  UserPlus,
  ChevronLeft,
  ChevronRight,
  RefreshCw
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Input } from '../../ui/input';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '../../ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../ui/select";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../../ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../ui/dialog";
import { Checkbox } from "../../ui/checkbox";
import { Label } from "../../ui/label";
import { toast } from 'sonner';
import { db } from '../../../lib/db';
import { formatDate } from '../../../lib/dateUtils';
import { v4 as uuidv4 } from 'uuid';

const leaveTypeInfo = {
  annual: { label: 'Annual Leave', icon: Palmtree, color: 'bg-blue-100 text-blue-600' },
  sick: { label: 'Sick Leave', icon: Heart, color: 'bg-red-100 text-red-600' },
  casual: { label: 'Casual Leave', icon: Briefcase, color: 'bg-amber-100 text-amber-600' },
  maternity: { label: 'Maternity Leave', icon: Baby, color: 'bg-pink-100 text-pink-600' },
  paternity: { label: 'Paternity Leave', icon: Baby, color: 'bg-cyan-100 text-cyan-600' },
  compensatory: { label: 'Comp Off', icon: Clock, color: 'bg-purple-100 text-purple-600' }
};

export default function AttendanceManager() {
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('attendance');
  
  // Bulk attendance state
  const [showBulkAddDialog, setShowBulkAddDialog] = useState(false);
  const [bulkAttendanceData, setBulkAttendanceData] = useState({
    selectedEmployees: [],
    selectedDates: [],
    status: 'present',
    checkInTime: '09:00',
    checkOutTime: '17:00',
    standardHoursPerDay: 8, // Standard hours before overtime kicks in
    overtimeMultiplier: 1.5, // 1.5x for overtime pay
    searchQuery: '' // Search filter for employees
  });
  const [bulkMonth, setBulkMonth] = useState(new Date().getMonth());
  const [bulkYear, setBulkYear] = useState(new Date().getFullYear());
  
  // Attendance state
  const [attendanceRecords, setAttendanceRecords] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState(new Date().toISOString().split('T')[0]);
  const [viewMode, setViewMode] = useState('daily');
  const [monthFilter, setMonthFilter] = useState(new Date().getMonth().toString());
  const [yearFilter, setYearFilter] = useState(new Date().getFullYear().toString());
  
  // Leave state
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [approvedLeaves, setApprovedLeaves] = useState([]);
  const [leaveSearchQuery, setLeaveSearchQuery] = useState('');
  const [leaveStatusFilter, setLeaveStatusFilter] = useState('all');
  const [leaveDeptFilter, setLeaveDeptFilter] = useState('all');
  
  // Detail modal
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedLeave, setSelectedLeave] = useState(null);

  useEffect(() => {
    loadData();
  }, [dateFilter, monthFilter, yearFilter, viewMode]);

  const loadData = async () => {
    setLoading(true);
    try {
      // Get all active employees
      const allUsers = await db.users.toArray();
      const activeEmployees = allUsers.filter(u => u.status === 'active' && u.role !== 'admin');
      setEmployees(activeEmployees);

      // Get attendance records
      let records = await db.attendanceRecords.toArray();
      
      // Get all leave requests
      let allLeaves = await db.leaveRequests.toArray();
      
      // Get approved leaves for attendance overlay
      let approvedLeavesData = allLeaves.filter(l => l.status === 'approved');

      const month = parseInt(monthFilter);
      const year = parseInt(yearFilter);
      const startOfMonth = new Date(year, month, 1).getTime();
      const endOfMonth = new Date(year, month + 1, 0, 23, 59, 59).getTime();

      if (viewMode === 'daily') {
        const selectedDate = new Date(dateFilter);
        selectedDate.setHours(0, 0, 0, 0);
        const selectedTimestamp = selectedDate.getTime();
        
        records = records.filter(r => {
          const recordDate = new Date(r.date);
          recordDate.setHours(0, 0, 0, 0);
          return recordDate.getTime() === selectedTimestamp;
        });
        
        approvedLeavesData = approvedLeavesData.filter(l => {
          // Normalize leave dates to start of day for comparison
          const leaveStart = new Date(l.startDate);
          leaveStart.setHours(0, 0, 0, 0);
          const leaveEnd = new Date(l.endDate);
          leaveEnd.setHours(23, 59, 59, 999); // End of day
          return selectedTimestamp >= leaveStart.getTime() && selectedTimestamp <= leaveEnd.getTime();
        });
      } else {
        records = records.filter(r => r.date >= startOfMonth && r.date <= endOfMonth);
        
        approvedLeavesData = approvedLeavesData.filter(l => {
          return l.startDate <= endOfMonth && l.endDate >= startOfMonth;
        });
      }

      // Filter leave requests by month/year
      const filteredLeaves = allLeaves.filter(r => {
        return r.startDate <= endOfMonth && r.endDate >= startOfMonth;
      });

      // Enrich records with employee data
      const enrichedRecords = records.map(r => ({
        ...r,
        employee: activeEmployees.find(e => e.id === r.userId)
      })).sort((a, b) => b.date - a.date);

      const enrichedLeaves = filteredLeaves.map(l => ({
        ...l,
        employee: activeEmployees.find(e => e.id === l.userId)
      })).sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));

      const enrichedApprovedLeaves = approvedLeavesData.map(l => ({
        ...l,
        employee: activeEmployees.find(e => e.id === l.userId)
      }));

      setAttendanceRecords(enrichedRecords);
      setLeaveRequests(enrichedLeaves);
      setApprovedLeaves(enrichedApprovedLeaves);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const departments = [...new Set(employees.map(e => e.profile?.department).filter(Boolean))];

  // Attendance filters
  const filteredRecords = attendanceRecords.filter(record => {
    const matchesSearch = searchQuery === '' ||
      record.employee?.profile?.firstName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      record.employee?.profile?.lastName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      record.employee?.email?.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesDepartment = departmentFilter === 'all' || 
      record.employee?.profile?.department === departmentFilter;
    
    const matchesStatus = statusFilter === 'all' || record.status === statusFilter;
    
    return matchesSearch && matchesDepartment && matchesStatus;
  });

  // Leave filters
  const filteredLeaves = leaveRequests.filter(request => {
    const matchesSearch = leaveSearchQuery === '' ||
      request.employee?.profile?.firstName?.toLowerCase().includes(leaveSearchQuery.toLowerCase()) ||
      request.employee?.profile?.lastName?.toLowerCase().includes(leaveSearchQuery.toLowerCase());
    
    const matchesStatus = leaveStatusFilter === 'all' || request.status === leaveStatusFilter;
    const matchesDept = leaveDeptFilter === 'all' || request.employee?.profile?.department === leaveDeptFilter;
    
    return matchesSearch && matchesStatus && matchesDept;
  });

  const getStatusBadge = (status) => {
    switch (status) {
      case 'present':
        return <Badge className="bg-emerald-100 text-emerald-700"><CheckCircle2 className="w-3 h-3 mr-1" />Present</Badge>;
      case 'late':
        return <Badge className="bg-amber-100 text-amber-700"><AlertCircle className="w-3 h-3 mr-1" />Late</Badge>;
      case 'absent':
        return <Badge className="bg-red-100 text-red-700"><XCircle className="w-3 h-3 mr-1" />Absent</Badge>;
      case 'half-day':
        return <Badge className="bg-orange-100 text-orange-700"><Clock className="w-3 h-3 mr-1" />Half Day</Badge>;
      case 'on-leave':
        return <Badge className="bg-blue-100 text-blue-700"><Calendar className="w-3 h-3 mr-1" />On Leave</Badge>;
      default:
        return <Badge className="bg-gray-100 text-gray-700">Unknown</Badge>;
    }
  };

  const getLeaveStatusBadge = (status) => {
    switch (status) {
      case 'approved':
        return <Badge className="bg-emerald-100 text-emerald-700"><CheckCircle2 className="w-3 h-3 mr-1" />Approved</Badge>;
      case 'rejected':
        return <Badge className="bg-red-100 text-red-700"><XCircle className="w-3 h-3 mr-1" />Rejected</Badge>;
      default:
        return <Badge className="bg-amber-100 text-amber-700"><Clock className="w-3 h-3 mr-1" />Pending</Badge>;
    }
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '-';
    return new Date(timestamp).toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: true 
    });
  };

  const formatHours = (hours) => {
    if (!hours || isNaN(hours)) return '-';
    const h = Math.floor(hours);
    const m = Math.round((hours - h) * 60);
    return `${h}h ${m}m`;
  };

  const formatDateDisplay = (timestamp) => {
    return new Date(timestamp).toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric'
    });
  };

  // Attendance stats
  const presentCount = filteredRecords.filter(r => r.status === 'present').length;
  const lateCount = filteredRecords.filter(r => r.status === 'late').length;
  const onLeaveCount = approvedLeaves.length;
  const avgWorkHours = filteredRecords.length > 0 
    ? filteredRecords.reduce((sum, r) => sum + (r.workHours || 0), 0) / filteredRecords.filter(r => r.workHours > 0).length || 0
    : 0;

  // Leave stats
  const pendingLeaveCount = leaveRequests.filter(r => r.status === 'pending').length;
  const approvedLeaveCount = leaveRequests.filter(r => r.status === 'approved').length;
  const rejectedLeaveCount = leaveRequests.filter(r => r.status === 'rejected').length;
  const totalLeaveDays = leaveRequests.filter(r => r.status === 'approved').reduce((sum, r) => sum + (r.days || 0), 0);

  const handleExportAttendance = () => {
    if (filteredRecords.length === 0 && approvedLeaves.length === 0) {
      toast.error('No data to export');
      return;
    }
    
    const attendanceData = filteredRecords.map(r => ({
      'Employee Name': `${r.employee?.profile?.firstName || ''} ${r.employee?.profile?.lastName || ''}`.trim(),
      'Department': r.employee?.profile?.department || '',
      'Date': formatDateDisplay(r.date),
      'Check In': formatTime(r.checkIn),
      'Check Out': formatTime(r.checkOut),
      'Work Hours': formatHours(r.workHours),
      'Status': r.status || 'absent',
      'Notes': r.notes || ''
    }));
    
    const leaveData = approvedLeaves.map(l => ({
      'Employee Name': `${l.employee?.profile?.firstName || ''} ${l.employee?.profile?.lastName || ''}`.trim(),
      'Department': l.employee?.profile?.department || '',
      'Date': `${formatDateDisplay(l.startDate)} - ${formatDateDisplay(l.endDate)}`,
      'Check In': '-',
      'Check Out': '-',
      'Work Hours': '-',
      'Status': 'on-leave',
      'Notes': l.reason || ''
    }));
    
    const exportData = [...attendanceData, ...leaveData];
    exportCSV(exportData, `attendance_${viewMode === 'daily' ? dateFilter : `${yearFilter}-${parseInt(monthFilter)+1}`}`);
    toast.success(`Exported ${exportData.length} attendance records`);
  };

  const handleExportLeaves = () => {
    if (filteredLeaves.length === 0) {
      toast.error('No data to export');
      return;
    }
    
    const exportData = filteredLeaves.map(r => ({
      'Employee Name': `${r.employee?.profile?.firstName || ''} ${r.employee?.profile?.lastName || ''}`.trim(),
      'Department': r.employee?.profile?.department || '',
      'Leave Type': leaveTypeInfo[r.leaveType]?.label || r.leaveType,
      'Start Date': formatDate(r.startDate),
      'End Date': formatDate(r.endDate),
      'Days': r.days || 0,
      'Status': r.status,
      'Reason': r.reason || '',
      'Requested On': formatDate(r.createdAt)
    }));

    exportCSV(exportData, `leave_requests_${yearFilter}-${parseInt(monthFilter)+1}`);
    toast.success(`Exported ${exportData.length} leave records`);
  };

  const exportCSV = (data, filename) => {
    if (!data.length) return;
    const headers = Object.keys(data[0]);
    const csvContent = [
      headers.join(','),
      ...data.map(row => headers.map(h => {
        const val = row[h];
        if (typeof val === 'string' && (val.includes(',') || val.includes('"'))) {
          return `"${val.replace(/"/g, '""')}"`;
        }
        return val;
      }).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `${filename}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
  };

  // Get employees without attendance for the day
  const getAbsentEmployees = () => {
    if (viewMode !== 'daily') return [];
    const checkedInIds = new Set(attendanceRecords.map(r => r.userId));
    const onLeaveIds = new Set(approvedLeaves.map(l => l.userId));
    return employees.filter(e => !checkedInIds.has(e.id) && !onLeaveIds.has(e.id));
  };

  const absentEmployees = getAbsentEmployees();

  const viewLeaveDetails = (leave) => {
    setSelectedLeave(leave);
    setShowDetailModal(true);
  };

  // Bulk attendance functions
  const daysInBulkMonth = useMemo(() => {
    return new Date(bulkYear, bulkMonth + 1, 0).getDate();
  }, [bulkMonth, bulkYear]);

  const handleBulkAddAttendance = async () => {
    if (bulkAttendanceData.selectedEmployees.length === 0) {
      toast.error('Please select at least one employee');
      return;
    }
    if (bulkAttendanceData.selectedDates.length === 0) {
      toast.error('Please select at least one date');
      return;
    }

    setLoading(true);
    try {
      const records = [];
      const checkInHours = parseInt(bulkAttendanceData.checkInTime.split(':')[0]);
      const checkInMins = parseInt(bulkAttendanceData.checkInTime.split(':')[1]);
      const checkOutHours = parseInt(bulkAttendanceData.checkOutTime.split(':')[0]);
      const checkOutMins = parseInt(bulkAttendanceData.checkOutTime.split(':')[1]);
      
      // Calculate total hours worked from check-in to check-out
      const totalMinutesWorked = (checkOutHours * 60 + checkOutMins) - (checkInHours * 60 + checkInMins);
      const totalHoursWorked = Math.max(0, totalMinutesWorked / 60);
      const standardHours = bulkAttendanceData.standardHoursPerDay || 8;
      const regularHours = Math.min(totalHoursWorked, standardHours);
      const overtimeHours = Math.max(0, totalHoursWorked - standardHours);

      for (const empId of bulkAttendanceData.selectedEmployees) {
        for (const dateStr of bulkAttendanceData.selectedDates) {
          // selectedDates contains ISO date strings like "2026-01-17"
          const date = new Date(dateStr + 'T00:00:00');
          date.setHours(0, 0, 0, 0);
          
          // Check if record already exists
          const existingRecords = await db.attendanceRecords
            .where('userId')
            .equals(empId)
            .filter(r => {
              const recordDate = new Date(r.date);
              recordDate.setHours(0, 0, 0, 0);
              return recordDate.getTime() === date.getTime();
            })
            .toArray();

          const checkIn = new Date(date);
          checkIn.setHours(checkInHours, checkInMins, 0, 0);
          
          const checkOut = new Date(date);
          checkOut.setHours(checkOutHours, checkOutMins, 0, 0);
          
          // Calculate work hours based on status
          let workHours = 0;
          let overtime = 0;
          if (bulkAttendanceData.status === 'present' || bulkAttendanceData.status === 'remote') {
            workHours = totalHoursWorked;
            overtime = overtimeHours;
          } else if (bulkAttendanceData.status === 'half-day') {
            workHours = Math.min(totalHoursWorked, standardHours / 2);
            overtime = 0;
          }

          if (existingRecords.length > 0) {
            // Update existing record
            await db.attendanceRecords.update(existingRecords[0].id, {
              status: bulkAttendanceData.status,
              checkIn: bulkAttendanceData.status !== 'absent' ? checkIn.getTime() : null,
              checkOut: bulkAttendanceData.status === 'present' || bulkAttendanceData.status === 'remote' ? checkOut.getTime() : null,
              workHours,
              overtime,
              overtimeMultiplier: bulkAttendanceData.overtimeMultiplier || 1.5,
              updatedAt: Date.now()
            });
          } else {
            // Create new record
            records.push({
              id: uuidv4(),
              userId: empId,
              date: date.getTime(),
              checkIn: bulkAttendanceData.status !== 'absent' ? checkIn.getTime() : null,
              checkOut: bulkAttendanceData.status === 'present' || bulkAttendanceData.status === 'remote' ? checkOut.getTime() : null,
              status: bulkAttendanceData.status,
              workHours,
              overtime,
              overtimeMultiplier: bulkAttendanceData.overtimeMultiplier || 1.5,
              createdAt: Date.now()
            });
          }
        }
      }

      if (records.length > 0) {
        await db.attendanceRecords.bulkAdd(records);
      }

      // Clean up any corrupted records with NaN dates
      const allRecords = await db.attendanceRecords.toArray();
      const corruptedIds = allRecords.filter(r => isNaN(r.date)).map(r => r.id);
      if (corruptedIds.length > 0) {
        await db.attendanceRecords.bulkDelete(corruptedIds);
      }

      toast.success(`Added/updated attendance for ${bulkAttendanceData.selectedEmployees.length} employees across ${bulkAttendanceData.selectedDates.length} days`);
      setShowBulkAddDialog(false);
      setBulkAttendanceData({
        selectedEmployees: [],
        selectedDates: [],
        status: 'present',
        checkInTime: '09:00',
        checkOutTime: '17:00',
        standardHoursPerDay: 8,
        overtimeMultiplier: 1.5,
        searchQuery: ''
      });
      loadData();
    } catch (error) {
      console.error('Error adding bulk attendance:', error);
      toast.error('Failed to add attendance records');
    } finally {
      setLoading(false);
    }
  };

  const toggleBulkEmployee = (empId) => {
    setBulkAttendanceData(prev => ({
      ...prev,
      selectedEmployees: prev.selectedEmployees.includes(empId)
        ? prev.selectedEmployees.filter(id => id !== empId)
        : [...prev.selectedEmployees, empId]
    }));
  };

  const toggleAllBulkEmployees = () => {
    setBulkAttendanceData(prev => ({
      ...prev,
      selectedEmployees: prev.selectedEmployees.length === employees.length
        ? []
        : employees.map(e => e.id)
    }));
  };

  const toggleBulkDate = (dateStr) => {
    console.log('toggleBulkDate called with:', dateStr);
    setBulkAttendanceData(prev => {
      const newDates = prev.selectedDates.includes(dateStr)
        ? prev.selectedDates.filter(d => d !== dateStr)
        : [...prev.selectedDates, dateStr];
      console.log('New selectedDates:', newDates);
      return {
        ...prev,
        selectedDates: newDates
      };
    });
  };

  const selectAllWorkingDays = () => {
    const workingDays = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    for (let d = 1; d <= daysInBulkMonth; d++) {
      const date = new Date(bulkYear, bulkMonth, d);
      // Skip weekends and future dates
      if (date.getDay() !== 0 && date.getDay() !== 6 && date <= today) {
        const dateStr = date.toISOString().split('T')[0];
        workingDays.push(dateStr);
      }
    }
    setBulkAttendanceData(prev => ({
      ...prev,
      selectedDates: workingDays
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Attendance & Leave Management</h1>
          <p className="text-gray-500 mt-1">Monitor employee attendance and manage leave requests</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => setShowBulkAddDialog(true)} className="bg-teal-600 hover:bg-teal-700">
            <Plus className="w-4 h-4 mr-2" /> Bulk Add Attendance
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <div className="flex items-center justify-between">
          <TabsList className="grid w-[400px] grid-cols-2">
            <TabsTrigger value="attendance" className="flex items-center gap-2">
              <Clock className="w-4 h-4" />
              Attendance
              {presentCount + lateCount > 0 && (
                <Badge variant="secondary" className="ml-1 bg-emerald-100 text-emerald-700 text-xs">
                  {presentCount + lateCount}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="leaves" className="flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              Leave Requests
              {pendingLeaveCount > 0 && (
                <Badge variant="secondary" className="ml-1 bg-amber-100 text-amber-700 text-xs">
                  {pendingLeaveCount}
                </Badge>
              )}
            </TabsTrigger>
          </TabsList>
          
          <Button variant="outline" onClick={activeTab === 'attendance' ? handleExportAttendance : handleExportLeaves}>
            <Download className="w-4 h-4 mr-2" /> Export
          </Button>
        </div>

        {/* Attendance Tab */}
        <TabsContent value="attendance" className="space-y-6 mt-6">
          {/* Summary Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-5 gap-4">
            <Card className="border-0 shadow-sm">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Total Employees</p>
                    <p className="text-2xl font-bold text-gray-900">{employees.length}</p>
                  </div>
                  <Users className="w-8 h-8 text-teal-600" />
                </div>
              </CardContent>
            </Card>
            <Card className="border-0 shadow-sm">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Present</p>
                    <p className="text-2xl font-bold text-emerald-600">{presentCount}</p>
                  </div>
                  <CheckCircle2 className="w-8 h-8 text-emerald-600" />
                </div>
              </CardContent>
            </Card>
            <Card className="border-0 shadow-sm">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Late</p>
                    <p className="text-2xl font-bold text-amber-600">{lateCount}</p>
                  </div>
                  <AlertCircle className="w-8 h-8 text-amber-600" />
                </div>
              </CardContent>
            </Card>
            <Card className="border-0 shadow-sm">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">On Leave</p>
                    <p className="text-2xl font-bold text-blue-600">{onLeaveCount}</p>
                  </div>
                  <Calendar className="w-8 h-8 text-blue-600" />
                </div>
              </CardContent>
            </Card>
            <Card className="border-0 shadow-sm">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Avg. Work Hours</p>
                    <p className="text-2xl font-bold text-purple-600">{formatHours(avgWorkHours)}</p>
                  </div>
                  <TrendingUp className="w-8 h-8 text-purple-600" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Filters */}
          <Card className="border-0 shadow-sm">
            <CardContent className="py-4">
              <div className="flex flex-col lg:flex-row gap-4">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    placeholder="Search employees..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10"
                  />
                </div>
                
                <Select value={viewMode} onValueChange={setViewMode}>
                  <SelectTrigger className="w-[150px]">
                    <Calendar className="w-4 h-4 mr-2" />
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="daily">Daily View</SelectItem>
                    <SelectItem value="monthly">Monthly View</SelectItem>
                  </SelectContent>
                </Select>

                {viewMode === 'daily' ? (
                  <Input
                    type="date"
                    value={dateFilter}
                    onChange={(e) => setDateFilter(e.target.value)}
                    className="w-[180px]"
                  />
                ) : (
                  <div className="flex gap-2">
                    <Select value={monthFilter} onValueChange={setMonthFilter}>
                      <SelectTrigger className="w-[140px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {['January', 'February', 'March', 'April', 'May', 'June', 
                          'July', 'August', 'September', 'October', 'November', 'December'
                        ].map((month, idx) => (
                          <SelectItem key={idx} value={idx.toString()}>{month}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Select value={yearFilter} onValueChange={setYearFilter}>
                      <SelectTrigger className="w-[100px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {[2023, 2024, 2025, 2026].map(year => (
                          <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                <Select value={departmentFilter} onValueChange={setDepartmentFilter}>
                  <SelectTrigger className="w-[180px]">
                    <Building2 className="w-4 h-4 mr-2" />
                    <SelectValue placeholder="Department" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Departments</SelectItem>
                    {departments.map(dept => (
                      <SelectItem key={dept} value={dept}>{dept}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-[150px]">
                    <Filter className="w-4 h-4 mr-2" />
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="present">Present</SelectItem>
                    <SelectItem value="late">Late</SelectItem>
                    <SelectItem value="absent">Absent</SelectItem>
                    <SelectItem value="half-day">Half Day</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Attendance Records Table */}
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle>Attendance Records</CardTitle>
              <CardDescription>
                {filteredRecords.length} record{filteredRecords.length !== 1 ? 's' : ''} found
              </CardDescription>
            </CardHeader>
            <CardContent>
              {filteredRecords.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Clock className="w-12 h-12 mx-auto mb-4 opacity-20" />
                  <p>No attendance records found for the selected period</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Employee</TableHead>
                      <TableHead>Department</TableHead>
                      {viewMode === 'monthly' && <TableHead>Date</TableHead>}
                      <TableHead>Check In</TableHead>
                      <TableHead>Check Out</TableHead>
                      <TableHead>Work Hours</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredRecords.map(record => (
                      <TableRow key={record.id}>
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 bg-gradient-to-br from-teal-400 to-emerald-500 rounded-full flex items-center justify-center text-white font-medium text-sm">
                              {record.employee?.profile?.firstName?.[0]}
                              {record.employee?.profile?.lastName?.[0]}
                            </div>
                            <div>
                              <p className="font-medium">
                                {record.employee?.profile?.firstName} {record.employee?.profile?.lastName}
                              </p>
                              <p className="text-xs text-gray-500">{record.employee?.email}</p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>{record.employee?.profile?.department || '-'}</TableCell>
                        {viewMode === 'monthly' && (
                          <TableCell>{formatDateDisplay(record.date)}</TableCell>
                        )}
                        <TableCell>
                          <div className="flex items-center gap-1 text-sm">
                            <LogIn className="w-3 h-3 text-emerald-500" />
                            {formatTime(record.checkIn)}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1 text-sm">
                            <LogOut className="w-3 h-3 text-red-500" />
                            {formatTime(record.checkOut)}
                          </div>
                        </TableCell>
                        <TableCell className="font-medium">{formatHours(record.workHours)}</TableCell>
                        <TableCell>{getStatusBadge(record.status)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>

          {/* Absent Employees */}
          {viewMode === 'daily' && absentEmployees.length > 0 && (
            <Card className="border-0 shadow-sm">
              <CardHeader>
                <CardTitle className="text-red-600">
                  <XCircle className="w-5 h-5 inline mr-2" />
                  Not Checked In Today
                </CardTitle>
                <CardDescription>
                  {absentEmployees.length} employee{absentEmployees.length !== 1 ? 's have' : ' has'} not checked in
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {absentEmployees.map(employee => (
                    <div key={employee.id} className="flex items-center gap-3 p-3 border rounded-lg">
                      <div className="w-10 h-10 bg-gradient-to-br from-gray-400 to-gray-500 rounded-full flex items-center justify-center text-white font-medium">
                        {employee.profile?.firstName?.[0]}
                        {employee.profile?.lastName?.[0]}
                      </div>
                      <div>
                        <p className="font-medium">
                          {employee.profile?.firstName} {employee.profile?.lastName}
                        </p>
                        <p className="text-sm text-gray-500">{employee.profile?.department}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Employees On Leave */}
          {approvedLeaves.length > 0 && (
            <Card className="border-0 shadow-sm">
              <CardHeader>
                <CardTitle className="text-blue-600">
                  <Calendar className="w-5 h-5 inline mr-2" />
                  On Approved Leave
                </CardTitle>
                <CardDescription>
                  {approvedLeaves.length} employee{approvedLeaves.length !== 1 ? 's are' : ' is'} on approved leave
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {approvedLeaves.map(leave => {
                    const typeInfo = leaveTypeInfo[leave.leaveType] || leaveTypeInfo.casual;
                    return (
                      <div key={leave.id} className="flex items-center gap-3 p-3 border rounded-lg bg-blue-50">
                        <div className="w-10 h-10 bg-gradient-to-br from-blue-400 to-indigo-500 rounded-full flex items-center justify-center text-white font-medium">
                          {leave.employee?.profile?.firstName?.[0]}
                          {leave.employee?.profile?.lastName?.[0]}
                        </div>
                        <div className="flex-1">
                          <p className="font-medium">
                            {leave.employee?.profile?.firstName} {leave.employee?.profile?.lastName}
                          </p>
                          <p className="text-sm text-gray-500">{leave.employee?.profile?.department}</p>
                          <div className="flex items-center gap-2 mt-1">
                            <Badge className={typeInfo.color + " text-xs"}>
                              {typeInfo.label}
                            </Badge>
                            <span className="text-xs text-gray-500">
                              {leave.days} day{leave.days !== 1 ? 's' : ''}
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Leave Requests Tab */}
        <TabsContent value="leaves" className="space-y-6 mt-6">
          {/* Summary Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-5 gap-4">
            <Card className="border-0 shadow-sm">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Total Requests</p>
                    <p className="text-2xl font-bold text-gray-900">{leaveRequests.length}</p>
                  </div>
                  <Calendar className="w-8 h-8 text-teal-600" />
                </div>
              </CardContent>
            </Card>
            <Card className="border-0 shadow-sm cursor-pointer hover:shadow-md transition-shadow" onClick={() => setLeaveStatusFilter('pending')}>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Pending</p>
                    <p className="text-2xl font-bold text-amber-600">{pendingLeaveCount}</p>
                  </div>
                  <Clock className="w-8 h-8 text-amber-600" />
                </div>
              </CardContent>
            </Card>
            <Card className="border-0 shadow-sm cursor-pointer hover:shadow-md transition-shadow" onClick={() => setLeaveStatusFilter('approved')}>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Approved</p>
                    <p className="text-2xl font-bold text-emerald-600">{approvedLeaveCount}</p>
                  </div>
                  <CheckCircle2 className="w-8 h-8 text-emerald-600" />
                </div>
              </CardContent>
            </Card>
            <Card className="border-0 shadow-sm cursor-pointer hover:shadow-md transition-shadow" onClick={() => setLeaveStatusFilter('rejected')}>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Rejected</p>
                    <p className="text-2xl font-bold text-red-600">{rejectedLeaveCount}</p>
                  </div>
                  <XCircle className="w-8 h-8 text-red-600" />
                </div>
              </CardContent>
            </Card>
            <Card className="border-0 shadow-sm">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Total Leave Days</p>
                    <p className="text-2xl font-bold text-blue-600">{totalLeaveDays}</p>
                  </div>
                  <Palmtree className="w-8 h-8 text-blue-600" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Filters */}
          <Card className="border-0 shadow-sm">
            <CardContent className="py-4">
              <div className="flex flex-col lg:flex-row gap-4">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    placeholder="Search employees..."
                    value={leaveSearchQuery}
                    onChange={(e) => setLeaveSearchQuery(e.target.value)}
                    className="pl-10"
                  />
                </div>
                
                <div className="flex gap-2">
                  <Select value={monthFilter} onValueChange={setMonthFilter}>
                    <SelectTrigger className="w-[140px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {['January', 'February', 'March', 'April', 'May', 'June', 
                        'July', 'August', 'September', 'October', 'November', 'December'
                      ].map((month, idx) => (
                        <SelectItem key={idx} value={idx.toString()}>{month}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select value={yearFilter} onValueChange={setYearFilter}>
                    <SelectTrigger className="w-[100px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[2023, 2024, 2025, 2026].map(year => (
                        <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <Select value={leaveDeptFilter} onValueChange={setLeaveDeptFilter}>
                  <SelectTrigger className="w-[180px]">
                    <Building2 className="w-4 h-4 mr-2" />
                    <SelectValue placeholder="Department" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Departments</SelectItem>
                    {departments.map(dept => (
                      <SelectItem key={dept} value={dept}>{dept}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select value={leaveStatusFilter} onValueChange={setLeaveStatusFilter}>
                  <SelectTrigger className="w-[150px]">
                    <Filter className="w-4 h-4 mr-2" />
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="approved">Approved</SelectItem>
                    <SelectItem value="rejected">Rejected</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Leave Requests Table */}
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle>Leave Requests</CardTitle>
              <CardDescription>
                {filteredLeaves.length} request{filteredLeaves.length !== 1 ? 's' : ''} found
              </CardDescription>
            </CardHeader>
            <CardContent>
              {filteredLeaves.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Calendar className="w-12 h-12 mx-auto mb-4 opacity-20" />
                  <p>No leave requests found for the selected period</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Employee</TableHead>
                      <TableHead>Department</TableHead>
                      <TableHead>Leave Type</TableHead>
                      <TableHead>Duration</TableHead>
                      <TableHead>Days</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Reason</TableHead>
                      <TableHead className="text-center">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredLeaves.map(request => {
                      const typeInfo = leaveTypeInfo[request.leaveType] || leaveTypeInfo.casual;
                      const TypeIcon = typeInfo.icon;
                      
                      return (
                        <TableRow key={request.id} className={request.status === 'pending' ? 'bg-amber-50/30' : ''}>
                          <TableCell>
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 bg-gradient-to-br from-teal-400 to-emerald-500 rounded-full flex items-center justify-center text-white font-medium text-sm">
                                {request.employee?.profile?.firstName?.[0]}
                                {request.employee?.profile?.lastName?.[0]}
                              </div>
                              <div>
                                <p className="font-medium">
                                  {request.employee?.profile?.firstName} {request.employee?.profile?.lastName}
                                </p>
                                <p className="text-xs text-gray-500">{request.employee?.email}</p>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>{request.employee?.profile?.department || '-'}</TableCell>
                          <TableCell>
                            <Badge className={typeInfo.color}>
                              <TypeIcon className="w-3 h-3 mr-1" />
                              {typeInfo.label}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-sm">
                            {formatDate(request.startDate)} - {formatDate(request.endDate)}
                          </TableCell>
                          <TableCell className="font-medium">{request.days}</TableCell>
                          <TableCell>{getLeaveStatusBadge(request.status)}</TableCell>
                          <TableCell className="max-w-[200px] truncate text-sm text-gray-500">
                            {request.reason || '-'}
                          </TableCell>
                          <TableCell className="text-center">
                            <Button variant="ghost" size="sm" onClick={() => viewLeaveDetails(request)}>
                              <Eye className="w-4 h-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Leave Detail Modal */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Leave Request Details</DialogTitle>
            <DialogDescription>
              View the complete leave request information
            </DialogDescription>
          </DialogHeader>

          {selectedLeave && (
            <div className="space-y-4">
              {/* Employee Info */}
              <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
                <div className="w-12 h-12 bg-gradient-to-br from-teal-400 to-emerald-500 rounded-full flex items-center justify-center text-white font-medium">
                  {selectedLeave.employee?.profile?.firstName?.[0]}
                  {selectedLeave.employee?.profile?.lastName?.[0]}
                </div>
                <div>
                  <p className="font-semibold text-gray-900">
                    {selectedLeave.employee?.profile?.firstName} {selectedLeave.employee?.profile?.lastName}
                  </p>
                  <p className="text-sm text-gray-500">
                    {selectedLeave.employee?.profile?.position} • {selectedLeave.employee?.profile?.department}
                  </p>
                </div>
              </div>

              {/* Leave Details */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Leave Type</p>
                  <Badge className={(leaveTypeInfo[selectedLeave.leaveType]?.color || 'bg-gray-100 text-gray-600') + " mt-1"}>
                    {leaveTypeInfo[selectedLeave.leaveType]?.label || selectedLeave.leaveType}
                  </Badge>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Status</p>
                  <div className="mt-1">{getLeaveStatusBadge(selectedLeave.status)}</div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Start Date</p>
                  <p className="font-medium">{formatDate(selectedLeave.startDate)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">End Date</p>
                  <p className="font-medium">{formatDate(selectedLeave.endDate)}</p>
                </div>
              </div>

              <div>
                <p className="text-sm text-gray-500">Duration</p>
                <p className="font-medium">{selectedLeave.days} day{selectedLeave.days !== 1 ? 's' : ''}</p>
              </div>

              {selectedLeave.reason && (
                <div>
                  <p className="text-sm text-gray-500 mb-1">Reason</p>
                  <p className="text-gray-900 bg-gray-50 p-3 rounded-lg">{selectedLeave.reason}</p>
                </div>
              )}

              {selectedLeave.status === 'rejected' && selectedLeave.rejectionReason && (
                <div className="p-3 bg-red-50 rounded-lg border border-red-200">
                  <p className="text-sm font-medium text-red-700">Rejection Reason:</p>
                  <p className="text-sm text-red-600">{selectedLeave.rejectionReason}</p>
                </div>
              )}

              <div className="text-xs text-gray-400 pt-2 border-t">
                Requested on {formatDate(selectedLeave.createdAt)}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Bulk Add Attendance Dialog */}
      <Dialog open={showBulkAddDialog} onOpenChange={setShowBulkAddDialog}>
        <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="w-5 h-5 text-teal-600" />
              Bulk Add Attendance
            </DialogTitle>
            <DialogDescription>
              Select employees and dates to add attendance records in bulk
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Month/Year Selector */}
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium text-gray-700">Month:</label>
                <Select
                  value={bulkMonth.toString()}
                  onValueChange={(val) => { setBulkMonth(parseInt(val)); setBulkAttendanceData(prev => ({ ...prev, selectedDates: [] })); }}
                >
                  <SelectTrigger className="w-32">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Array.from({ length: 12 }, (_, i) => (
                      <SelectItem key={i} value={i.toString()}>
                        {new Date(2024, i).toLocaleString('default', { month: 'long' })}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium text-gray-700">Year:</label>
                <Select
                  value={bulkYear.toString()}
                  onValueChange={(val) => { setBulkYear(parseInt(val)); setBulkAttendanceData(prev => ({ ...prev, selectedDates: [] })); }}
                >
                  <SelectTrigger className="w-24">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[2024, 2025, 2026].map(year => (
                      <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Employee Selection */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-gray-700">Select Employees</label>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={toggleAllBulkEmployees}
                  className="text-teal-600 hover:text-teal-700"
                >
                  {bulkAttendanceData.selectedEmployees.length === employees.length ? 'Deselect All' : 'Select All'}
                </Button>
              </div>
              
              {/* Employee Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  type="text"
                  placeholder="Search employees..."
                  value={bulkAttendanceData.searchQuery}
                  onChange={(e) => setBulkAttendanceData(prev => ({ ...prev, searchQuery: e.target.value }))}
                  className="pl-9"
                />
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2 max-h-40 overflow-y-auto p-3 border rounded-lg bg-gray-50">
                {employees
                  .filter(emp => {
                    if (!emp.profile) return false;
                    const searchLower = (bulkAttendanceData.searchQuery || '').toLowerCase();
                    const fullName = `${emp.profile.firstName || ''} ${emp.profile.lastName || ''}`.toLowerCase();
                    const department = (emp.profile.department || '').toLowerCase();
                    return fullName.includes(searchLower) || department.includes(searchLower);
                  })
                  .map(emp => (
                  <div key={emp.id} className="flex items-center gap-2">
                    <Checkbox
                      id={`bulk-emp-${emp.id}`}
                      checked={bulkAttendanceData.selectedEmployees.includes(emp.id)}
                      onCheckedChange={() => toggleBulkEmployee(emp.id)}
                    />
                    <Label htmlFor={`bulk-emp-${emp.id}`} className="text-sm cursor-pointer">
                      {emp.profile?.firstName} {emp.profile?.lastName}
                    </Label>
                  </div>
                ))}
              </div>
              <p className="text-xs text-gray-500">
                {bulkAttendanceData.selectedEmployees.length} of {employees.length} employees selected
                {bulkAttendanceData.searchQuery && ` (showing ${employees.filter(emp => {
                  if (!emp.profile) return false;
                  const searchLower = (bulkAttendanceData.searchQuery || '').toLowerCase();
                  const fullName = `${emp.profile.firstName || ''} ${emp.profile.lastName || ''}`.toLowerCase();
                  const department = (emp.profile.department || '').toLowerCase();
                  return fullName.includes(searchLower) || department.includes(searchLower);
                }).length} matching)`}
              </p>
            </div>

            {/* Date Selection Calendar */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-gray-700">Select Dates</label>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={selectAllWorkingDays}
                  className="text-teal-600 hover:text-teal-700"
                >
                  Select All Working Days (Mon-Fri)
                </Button>
              </div>
              <div className="border rounded-lg p-4 bg-gray-50">
                <div className="grid grid-cols-7 gap-1 text-center mb-2">
                  {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                    <div key={day} className="text-xs font-medium text-gray-500 py-1">{day}</div>
                  ))}
                </div>
                <div className="grid grid-cols-7 gap-1">
                  {(() => {
                    const firstDay = new Date(bulkYear, bulkMonth, 1);
                    const lastDay = new Date(bulkYear, bulkMonth + 1, 0);
                    const startPadding = firstDay.getDay();
                    const daysInMonth = lastDay.getDate();
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);

                    const cells = [];
                    // Add empty cells for padding
                    for (let i = 0; i < startPadding; i++) {
                      cells.push(<div key={`pad-${i}`} className="h-8" />);
                    }
                    // Add day cells
                    for (let day = 1; day <= daysInMonth; day++) {
                      const date = new Date(bulkYear, bulkMonth, day);
                      const dateStr = date.toISOString().split('T')[0];
                      const isWeekend = date.getDay() === 0 || date.getDay() === 6;
                      const isFuture = date > today;
                      const isSelected = bulkAttendanceData.selectedDates.includes(dateStr);

                      cells.push(
                        <button
                          key={day}
                          type="button"
                          disabled={isFuture}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            toggleBulkDate(dateStr);
                          }}
                          className={`h-8 w-full text-sm rounded transition-colors cursor-pointer ${
                            isSelected
                              ? 'bg-teal-500 text-white font-medium'
                              : isFuture
                              ? 'text-gray-300 cursor-not-allowed'
                              : isWeekend
                              ? 'text-gray-400 hover:bg-gray-200'
                              : 'text-gray-700 hover:bg-teal-100'
                          }`}
                        >
                          {day}
                        </button>
                      );
                    }
                    return cells;
                  })()}
                </div>
              </div>
              <p className="text-xs text-gray-500">
                {bulkAttendanceData.selectedDates.length} date(s) selected
              </p>
            </div>

            {/* Attendance Details */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">Status</label>
                <Select
                  value={bulkAttendanceData.status}
                  onValueChange={(val) => setBulkAttendanceData(prev => ({ ...prev, status: val }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="present">Present</SelectItem>
                    <SelectItem value="absent">Absent</SelectItem>
                    <SelectItem value="half-day">Half Day</SelectItem>
                    <SelectItem value="on-leave">On Leave</SelectItem>
                    <SelectItem value="remote">Remote/WFH</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">Check-in Time</label>
                <Input
                  type="time"
                  value={bulkAttendanceData.checkInTime}
                  onChange={(e) => setBulkAttendanceData(prev => ({ ...prev, checkInTime: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">Check-out Time</label>
                <Input
                  type="time"
                  value={bulkAttendanceData.checkOutTime}
                  onChange={(e) => setBulkAttendanceData(prev => ({ ...prev, checkOutTime: e.target.value }))}
                />
              </div>
            </div>

            {/* Overtime Settings */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">Standard Hours Per Day</label>
                <Input
                  type="number"
                  min="1"
                  max="24"
                  step="0.5"
                  value={bulkAttendanceData.standardHoursPerDay}
                  onChange={(e) => setBulkAttendanceData(prev => ({ ...prev, standardHoursPerDay: parseFloat(e.target.value) || 8 }))}
                />
                <p className="text-xs text-gray-500">Hours beyond this are counted as overtime</p>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">Overtime Multiplier</label>
                <Select
                  value={bulkAttendanceData.overtimeMultiplier?.toString() || '1.5'}
                  onValueChange={(val) => setBulkAttendanceData(prev => ({ ...prev, overtimeMultiplier: parseFloat(val) }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1.25">1.25x (Time and a quarter)</SelectItem>
                    <SelectItem value="1.5">1.5x (Time and a half)</SelectItem>
                    <SelectItem value="1.75">1.75x</SelectItem>
                    <SelectItem value="2">2x (Double time)</SelectItem>
                    <SelectItem value="2.5">2.5x (Holiday OT)</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-500">Applied to hourly rate for overtime hours</p>
              </div>
            </div>

            {/* Calculated Hours Preview */}
            {(() => {
              const checkInParts = bulkAttendanceData.checkInTime.split(':');
              const checkOutParts = bulkAttendanceData.checkOutTime.split(':');
              const checkInMins = parseInt(checkInParts[0]) * 60 + parseInt(checkInParts[1]);
              const checkOutMins = parseInt(checkOutParts[0]) * 60 + parseInt(checkOutParts[1]);
              const totalMinutes = Math.max(0, checkOutMins - checkInMins);
              const totalHours = totalMinutes / 60;
              const standardHours = bulkAttendanceData.standardHoursPerDay || 8;
              const regularHours = Math.min(totalHours, standardHours);
              const overtimeHours = Math.max(0, totalHours - standardHours);
              
              return (
                <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <p className="text-sm font-medium text-blue-900 mb-2">Hours Calculation Preview</p>
                  <div className="grid grid-cols-3 gap-4 text-sm">
                    <div>
                      <span className="text-blue-600">Total Hours:</span>
                      <span className="ml-2 font-medium">{totalHours.toFixed(2)}h</span>
                    </div>
                    <div>
                      <span className="text-green-600">Regular Hours:</span>
                      <span className="ml-2 font-medium">{regularHours.toFixed(2)}h</span>
                    </div>
                    <div>
                      <span className="text-orange-600">Overtime Hours:</span>
                      <span className="ml-2 font-medium">{overtimeHours.toFixed(2)}h</span>
                      {overtimeHours > 0 && (
                        <span className="text-xs text-orange-500 ml-1">@ {bulkAttendanceData.overtimeMultiplier}x</span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })()}
            {/* Summary */}
            {bulkAttendanceData.selectedEmployees.length > 0 && bulkAttendanceData.selectedDates.length > 0 && (
              <div className="p-4 bg-teal-50 rounded-lg border border-teal-200">
                <p className="text-sm text-teal-800">
                  <span className="font-medium">Summary:</span> This will create/update{' '}
                  <span className="font-bold">{bulkAttendanceData.selectedEmployees.length * bulkAttendanceData.selectedDates.length}</span>{' '}
                  attendance records ({bulkAttendanceData.selectedEmployees.length} employees × {bulkAttendanceData.selectedDates.length} dates)
                </p>
              </div>
            )}
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowBulkAddDialog(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleBulkAddAttendance}
              disabled={bulkAttendanceData.selectedEmployees.length === 0 || bulkAttendanceData.selectedDates.length === 0}
              className="bg-teal-600 hover:bg-teal-700"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add {bulkAttendanceData.selectedEmployees.length * bulkAttendanceData.selectedDates.length} Records
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
